import { BaseDomainModel } from '../models/base/base-domain-model';
import { inject, Injectable } from '@angular/core';
import { MembersAPI } from '../api/members-api';
import { UserDomainModel } from './user-domain-model';
import { EmployerUser } from '../models/users/dto/employer-user';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { MemberUser } from '../models/users/dto/member-user';
import { CreateMemberRequest } from '../models/users/requests/create-member-request';
import { SessionService } from '../services/session-service';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { ChangeEmailRequest } from '../models/users/requests/change-email-request';
import { HttpResponse } from '@angular/common/http';
import { MemberUserSearchResult } from '../models/users/dto/member-user-search-result';
import { MemberRecordCommentRequest } from '../models/users/requests/member-record-comment-request';
import { MemberRecordComment } from '../views/records/components/members/ip-member-record/member-record-comments/member-record-comment';
import { Mentionable } from '../models/base/mentionable';
import { ODataResponse } from '../models/protocols/odata-response';
import { EmploymentHistory } from '../models/users/dto/employment-history';
import { EmployeeTableUser } from '../models/users/dto/employee-table-user';
import { ResendMemberInviteRequest } from '../models/users/requests/resend-member-invite-request';
import { MostRecentInvite } from '../models/users/dto/most-recent-invite';
import { MemberRecord } from '../models/users/dto/member-record';
import { PortalService } from '../services/portal/portal.service';
import { PortalType } from '../models/enum/shared/portal-type';
import { MemberSpouseContactInfoRequest } from '../models/users/requests/member-spouse-contact-info-request';
import { MemberRelationshipStatus } from '../models/users/dto/member-relationship-status';
import { IpMemberSpouseContactInfoRequest } from '../models/users/requests/ip-member-spouse-contact-info-request';
import { MemberPersonalInformationOnboardingRequest } from '../models/users/requests/member-personal-information-onboarding-request';
import { InternalUserCommentReadReceiptRequest } from '../models/users/requests/internal-user-comment-read-receipt-request';
import { MemberOnboardingProgress } from '../models/members/member-onboarding-progress';
import { NewMemberVerificationRequest } from '../models/members/new-member-verification-request';
import { NewMemberVerificationResponse } from '../models/members/new-member-verification-response';
import { OdataUtils } from '../utils/odata-utils';

// Provided by Logged In Scope
@Injectable()
export class MembersDomainModel extends BaseDomainModel {
  constructor(private membersAPI: MembersAPI, private session: SessionService) {
    super();
    this.init();
  }

  private portalService = inject(PortalService);
  private userDomainModel = inject(UserDomainModel);

  private readonly employerUser$ = this.userDomainModel.user$.pipe(map(user => user as EmployerUser));
  private employerId$ = this.employerUser$.pipe(map(user => user?.employerId.toString()));

  private _allMemberUsers = new BehaviorSubject<MemberUser[]>([]);
  public allMemberUsers$ = this._allMemberUsers as Observable<MemberUser[]>;

  private _allMemberUsersTotalCount = new BehaviorSubject<number>(0);
  public allMemberUsersTotalCount$ = this._allMemberUsersTotalCount as Observable<number>;

  private _employerMemberUsers = new BehaviorSubject<EmployeeTableUser[]>([]);
  public employerMemberUsers$ = this._employerMemberUsers as Observable<EmployeeTableUser[]>;

  private _employerMemberUsersTotalCount = new BehaviorSubject<number>(0);
  public employerMemberUsersTotalCount$ = this._employerMemberUsersTotalCount as Observable<number>;

  public createMember(req: CreateMemberRequest): Observable<MemberUser> {
    return this.membersAPI.ipCreateMember(req);
  }

  public addCommentToMemberRecord(req: MemberRecordCommentRequest): Observable<MemberRecordComment[]> {
    return this.membersAPI.addCommentToMemberRecord(req).pipe(
      map(c => {
        return c;
      })
    );
  }

  public updateMemberOnboardingInformation(req: MemberPersonalInformationOnboardingRequest): Observable<MemberUser> {
    return this.membersAPI.updateMemberOnboardingInformation(req).pipe(
      map(u => {
        this.session.updateUser(u);
        return u;
      })
    );
  }

  public mpGetMember(odataQueryOptions: ODataQueryOptions): Observable<MemberUser> {
    return this.membersAPI.mpGetMember(odataQueryOptions);
  }

  public internalPortalGetMemberById(memberId: number, odataQueryOptions: ODataQueryOptions): Observable<MemberRecord> {
    return this.membersAPI.ipGetMemberById(memberId, odataQueryOptions);
  }

  public employerPortalGetMemberById(memberId: number, odataQueryOptions: ODataQueryOptions): Observable<MemberUser> {
    return this.membersAPI.epGetMemberById(memberId, odataQueryOptions);
  }

  public getMemberCommentMentionables(odataQueryOptions: ODataQueryOptions): Observable<Mentionable[]> {
    return this.membersAPI.getMemberCommentMentionables(odataQueryOptions).pipe(map(r => r.value));
  }

  public ipGetNewMembersToVerify(req: NewMemberVerificationRequest[]): Observable<NewMemberVerificationResponse[]> {
    return this.membersAPI.ipGetNewMembersToVerify(req);
  }

  public getAllFilteredMemberNames(filterString: string): Observable<MemberUser[]> {
    const queryOptions = new ODataQueryOptions();
    queryOptions.setFilter(
      `contains(firstName, '${OdataUtils.escapeStringLiteral(filterString)}')` +
        `or contains(lastName, '${OdataUtils.escapeStringLiteral(filterString)}')`
    );
    return this.membersAPI.getAllMembers(queryOptions).pipe(map(res => res.value));
  }

  public getAllFilteredMemberNumbers(filterString: string): Observable<MemberUser[]> {
    const queryOptions = new ODataQueryOptions();
    queryOptions.setFilter(`contains(cast(Id, 'Edm.String'),'${OdataUtils.escapeStringLiteral(filterString)}')`);
    return this.membersAPI.getAllMembers(queryOptions).pipe(map(res => res.value));
  }

  public getAllMemberUsers(oDataParams: ODataQueryOptions): Observable<ODataResponse<MemberUser>> {
    return this.membersAPI.getAllMembers(oDataParams).pipe(
      tap(response => {
        this._allMemberUsers.next(response.value);
        this._allMemberUsersTotalCount.next(response['@odata.count'] ?? 0);
      }),
      catchError(err => {
        this._allMemberUsers.next([]);
        this._allMemberUsersTotalCount.next(0);
        return throwError(() => err);
      })
    );
  }

  public getEmployerFilteredMemberNames(filterString: string): Observable<EmployeeTableUser[]> {
    const queryOptions = new ODataQueryOptions();
    queryOptions.setFilter(
      `contains(firstName, '${OdataUtils.escapeStringLiteral(filterString)}')` +
        `or contains(lastName, '${OdataUtils.escapeStringLiteral(filterString)}')`
    );
    return this.membersAPI.epGetEmployerMembers(queryOptions).pipe(map(res => res.value));
  }

  public getEmployerFilteredMemberNumbers(filterString: string): Observable<EmployeeTableUser[]> {
    const queryOptions = new ODataQueryOptions();
    queryOptions.setFilter(`contains(cast(Id, 'Edm.String'),'${OdataUtils.escapeStringLiteral(filterString)}')`);
    return this.membersAPI.epGetEmployerMembers(queryOptions).pipe(map(res => res.value));
  }

  public getEmployerMemberUsers(oDataParams: ODataQueryOptions): Observable<ODataResponse<EmployeeTableUser>> {
    return this.membersAPI.epGetEmployerMembers(oDataParams).pipe(
      tap(response => {
        this._employerMemberUsers.next(response.value);
        this._employerMemberUsersTotalCount.next(response['@odata.count'] ?? 0);
      }),
      catchError(err => {
        this._employerMemberUsers.next([]);
        this._employerMemberUsersTotalCount.next(0);
        return throwError(() => err);
      })
    );
  }

  public forceChangeEmail(req: ChangeEmailRequest, memberId: number): Observable<MemberUser> {
    return this.membersAPI.forceChangeEmail(memberId, req);
  }

  public forceChangePassword(memberId: string): Observable<HttpResponse<any>> {
    return this.membersAPI.forceChangePassword(memberId);
  }

  public searchMembersByIdOrName(searchString: string, employerId?: number): Observable<MemberUserSearchResult[]> {
    const queryOptions = new ODataQueryOptions();
    const searchStringIsId = !isNaN(Number(searchString));
    if (searchStringIsId) {
      queryOptions.setFilter(`id eq ${searchString}`);
    } else {
      const escapedSearchString = OdataUtils.escapeStringLiteral(searchString);
      queryOptions.setFilter(`contains(Name, '${escapedSearchString}')`);
    }
    return this.searchMembers(queryOptions, employerId);
  }

  public searchMembersByName(searchString: string, employerId?: number): Observable<MemberUserSearchResult[]> {
    const queryOptions = new ODataQueryOptions();
    const escapedSearchString = OdataUtils.escapeStringLiteral(searchString);
    queryOptions.setFilter(`contains(Name, '${escapedSearchString}')`);
    return this.searchMembers(queryOptions, employerId);
  }

  private searchMembers(queryOptions: ODataQueryOptions, employerId?: number): Observable<MemberUserSearchResult[]> {
    return this.portalService.isInternal$.pipe(
      take(1),
      switchMap(isInternal => {
        if (isInternal) {
          return this.membersAPI.ipSearchMembersByIdOrName(queryOptions, employerId);
        } else {
          return this.membersAPI.epSearchMembersByIdOrName(queryOptions, employerId);
        }
      }),
      map(r => r.value)
    );
  }

  public getMemberEmploymentHistory(memberId: number): Observable<EmploymentHistory[]> {
    return this.portalService.portalType$.pipe(
      take(1),
      switchMap(portalType => {
        switch (portalType) {
          case PortalType.Member:
            return this.membersAPI.getMemberEmploymentHistory();
          case PortalType.Internal:
            return this.membersAPI.getInternalMemberEmploymentHistory(memberId);
          case PortalType.Employer:
            return this.membersAPI.getEmployerMemberEmploymentHistory(memberId);
        }
      })
    );
  }

  public createMemberEmploymentHistory(req: EmploymentHistory): Observable<EmploymentHistory> {
    return this.membersAPI.createInternalMemberEmploymentHistory(req, req.memberId);
  }

  public updateEmployerHistory(req: EmploymentHistory): Observable<EmploymentHistory> {
    return this.portalService.isInternal$.pipe(
      take(1),
      switchMap(isInternal => {
        if (isInternal) {
          return this.membersAPI.updateInternalMemberEmploymentHistory(req);
        } else {
          return this.membersAPI.updateEmployerMemberEmploymentHistory(req);
        }
      })
    );
  }

  public resendMemberInvite(memberId: string, req: ResendMemberInviteRequest): Observable<MostRecentInvite> {
    return this.membersAPI.resendMemberInvite(memberId, req);
  }

  public getCommentsForMember(memberId: string): Observable<MemberRecordComment[]> {
    return this.membersAPI.getCommentsForMember(memberId).pipe(
      map(res => {
        return res.value;
      })
    );
  }

  public mpGetMemberOnboardingProgress(opts: ODataQueryOptions): Observable<MemberOnboardingProgress> {
    return this.membersAPI.mpGetMemberOnboardingProgress(opts);
  }

  public mpUpdateMemberSpouseContactInfo(req: MemberSpouseContactInfoRequest): Observable<MemberRelationshipStatus> {
    return this.membersAPI.mpUpdateMemberSpouseContactInfo(req);
  }

  public ipUpdateMemberSpouseContactInfo(req: IpMemberSpouseContactInfoRequest): Observable<MemberRelationshipStatus> {
    return this.membersAPI.ipUpdateMemberSpouseContactInfo(req);
  }

  public ipCreateMembers(req: CreateMemberRequest[]): Observable<MemberUser[]> {
    return this.membersAPI.ipCreateNewMembers(req);
  }

  public epCreateEmployees(req: CreateMemberRequest[]): Observable<HttpResponse<any>> {
    return this.membersAPI.epCreateEmployees(req);
  }

  public ipSetMemberRecordCommentsAsRead(req: InternalUserCommentReadReceiptRequest): Observable<HttpResponse<any>> {
    return this.membersAPI.ipSetMemberRecordCommentsAsRead(req);
  }
}
